/* THE CORE BUILD, NOT THE BARREL. `from 'highlight.js'` is every language the
   library ships - about 190 of them, 930 KB raw and 287 KB over the wire - and
   the six lines under it then register the six we actually use. It was the
   heaviest thing on a documentation page by a factor of three, on the pages
   whose whole job is to rank and be cited. `lib/core` is the same API with no
   languages preloaded, which is exactly what a registerLanguage list means. */
import { highlightCode } from './docs/code-highlight'

/**
 * THE TWO BODIES THIS FILE DRESSES (pass 398.89).
 *
 * `.doc-content` is a product's documentation; `.entry-content` is a Knowledge
 * Base article, which is written in the SAME editor through the SAME pipeline
 * and therefore stores the same `<pre class="code-block" data-filename>` and
 * `language-*` markup. Only the docs page ever loaded this file, so every KB
 * snippet - in a base that is entirely technical - printed as flat grey text
 * with no language, no filename and nothing to copy it with.
 *
 * The tab initialiser is in the list too, and it could not be until the block
 * layer in `components/doc-content.scss` was widened to both bodies: this script
 * HIDES the panels it does not show, so on a body with no tab CSS it would have
 * left a group with every panel hidden and no strip to bring one back.
 */
const CONTENT_ROOTS = '.doc-content, .entry-content'

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.doc-content pre code, .entry-content pre code').forEach(block => {
        highlightCode(block)
    })

    // ── Color swatch decorator ──
    document.querySelectorAll(CONTENT_ROOTS).forEach(decorateColorValues)

    // ── Tabs (div[data-type="tabs"]) ──
    document.querySelectorAll('.doc-content div[data-type="tabs"], .entry-content div[data-type="tabs"]').forEach(initTabs)

    // ── Heading anchors ──
    document.querySelectorAll(CONTENT_ROOTS).forEach(upgradeHeadingAnchors)

    // ── Comparison bars (pass 400.66) ──
    animateBars(document.querySelectorAll('.doc-content div[data-type="bars"], .entry-content div[data-type="bars"]'))
})

/**
 * Grow each comparison block's bars from zero the first time it scrolls into view.
 * The lengths themselves are server-rendered; this only withholds them until the
 * reader can see them move. No observer, or reduced motion: nothing is marked and
 * every bar just stands at its length.
 */
function animateBars(blocks) {
    if (!blocks.length || !('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const io = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return
        entry.target.classList.add('is-in')
        io.unobserve(entry.target)
    }), {threshold: 0.4})

    blocks.forEach(block => {
        block.dataset.barsAnimate = ''
        io.observe(block)
    })
}

/**
 * Turn the heading's `#` anchor into a copy-link button (pass 388).
 *
 * The anchor is ALREADY THERE - `App\Support\DocHeadingAnchors` writes an
 * `<a class="doc-anchor" href="#id">#</a>` into the stored HTML on save, and the
 * TOC links to the same ids. This does not add a control; it upgrades the one
 * that exists, which is why it is an `<a>` and stays one: with JS off it is a
 * working in-page link, and the reader loses the copy, not the anchor.
 *
 * Two things change. The `#` becomes a link glyph, because a bare hash beside a
 * heading reads as punctuation the author typed. And a click copies the ABSOLUTE
 * url rather than only moving the hash - somebody clicking it is going to paste
 * it somewhere else, where `#installation` on its own is worthless.
 *
 * `preventDefault` only when the copy actually succeeds: if the clipboard is
 * unavailable (plain http on a dev box) the anchor does what an anchor does.
 */
function upgradeHeadingAnchors(root) {
    root.querySelectorAll('.doc-anchor').forEach(anchor => {
        if (anchor.dataset.upgraded) return
        anchor.dataset.upgraded = '1'

        const icon = document.createElement('i')
        icon.className = 'ti ti-link'
        icon.setAttribute('aria-hidden', 'true')
        anchor.replaceChildren(icon)

        const heading = anchor.closest('h2, h3, h4')
        if (heading) {
            // `textContent` now excludes the glyph, so the label is the heading.
            anchor.setAttribute('aria-label', `Copy link to "${heading.textContent.trim()}"`)
            anchor.setAttribute('title', 'Copy link to this section')
        }

        anchor.addEventListener('click', event => {
            const url = `${location.origin}${location.pathname}${anchor.getAttribute('href')}`

            // ── IT USED TO GIVE UP OUTSIDE A SECURE CONTEXT ────────────────
            // `if (!navigator.clipboard || !window.isSecureContext) return` was
            // the whole handler on plain http: no copy, no confirmation, and the
            // anchor just moved the hash - so clicking it did nothing a reader
            // could see. `copyText` falls back to `execCommand`, which is
            // deprecated and works there; only if BOTH fail does the anchor stay
            // a plain in-page link, which is what it is with JS off anyway.
            if (! copyText(url)) return

            event.preventDefault()

            anchor.classList.add('is-copied')
            icon.className = 'ti ti-check'
            setTimeout(() => {
                anchor.classList.remove('is-copied')
                icon.className = 'ti ti-link'
            }, 1600)
        })
    })
}


/**
 * Put a string on the clipboard, and say whether it landed.
 *
 * TWO WAYS TO REACH A CLIPBOARD, AND THE OLD ONE IS NOT OPTIONAL.
 * `navigator.clipboard` exists only in a SECURE CONTEXT - on plain http (every
 * local dev host, an internal preview, an intranet mirror) the property is
 * undefined. `execCommand('copy')` is deprecated and works there.
 *
 * SYNCHRONOUS, deliberately: `execCommand` needs TRANSIENT ACTIVATION, so it has
 * to run in the same turn as the click that triggered it. An `await` anywhere
 * before it spends that activation and the copy silently returns false.
 */
function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).catch(() => {})

        return true
    }

    const ta = document.createElement('textarea')
    ta.value = text
    // Off-screen rather than hidden: `display: none` cannot be selected, and a
    // visible one would scroll the page to itself on focus.
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    ta.setAttribute('readonly', '')
    document.body.appendChild(ta)
    ta.select()

    let ok = false
    try { ok = document.execCommand('copy') } catch (e) { ok = false }

    document.body.removeChild(ta)

    return ok
}

/**
 * Turn a stored `<div data-type="tabs">` into an interactive tab set.
 *
 * The stored markup is only the panels - one `<div data-type="tab" data-label>` per tab.
 * The tab strip is built HERE, at read time, and never persisted (the purifier would
 * strip a `<button>` anyway). So with JS off, the PDF, or the editor, every panel is
 * simply visible in order - the content is never hidden behind a control that cannot run.
 */
function initTabs(tabsEl) {
    const panels = Array.from(tabsEl.querySelectorAll(':scope > [data-type="tab"]'))
    if (!panels.length || tabsEl.querySelector(':scope > [data-tablist]')) return

    const strip = document.createElement('div')
    strip.setAttribute('data-tablist', '')
    strip.setAttribute('role', 'tablist')

    const buttons = panels.map((panel, i) => {
        const btn = document.createElement('button')
        btn.type = 'button'
        btn.textContent = panel.getAttribute('data-label') || `Tab ${i + 1}`
        btn.setAttribute('role', 'tab')
        btn.setAttribute('data-tab-btn', '')
        btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false')
        panel.setAttribute('role', 'tabpanel')
        panel.hidden = i !== 0
        strip.appendChild(btn)
        return btn
    })

    const select = (idx) => {
        panels.forEach((panel, i) => { panel.hidden = i !== idx })
        buttons.forEach((btn, i) => btn.setAttribute('aria-selected', i === idx ? 'true' : 'false'))
    }

    buttons.forEach((btn, i) => {
        btn.addEventListener('click', () => select(i))
        // Left / right arrows move between tabs, per the WAI-ARIA tabs pattern.
        btn.addEventListener('keydown', (e) => {
            if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
            e.preventDefault()
            const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length
            select(next)
            buttons[next].focus()
        })
    })

    tabsEl.insertBefore(strip, tabsEl.firstChild)
}

/**
 * Find hex (#RGB, #RRGGBB, #RRGGBBAA) and rgb/rgba() values in text nodes
 * and wrap them in an inline swatch element: [■] #A3A3A3
 *
 * Safety: skips pre, code, a, script, style, textarea, input, [contenteditable]
 */
function decorateColorValues(root) {
    const SKIP_TAGS = new Set(['PRE', 'A', 'SCRIPT', 'STYLE', 'TEXTAREA', 'INPUT', 'BUTTON'])
    // Match #RGB, #RRGGBB, #RRGGBBAA or rgb()/rgba() with numbers/spaces/commas/percentages/decimals
    const COLOR_RE = /#(?:[0-9a-fA-F]{3,4}){1,2}\b|rgba?\(\s*[\d\s,./%]+\s*\)/g

    // ── Pass 1: <code> elements that contain ONLY a color value > replace entire <code> ──
    const codesToReplace = []
    root.querySelectorAll(':not(pre) > code').forEach(code => {
        const text = code.textContent.trim()
        COLOR_RE.lastIndex = 0
        const m = text.match(COLOR_RE)
        if (m && m.length === 1 && m[0] === text) {
            codesToReplace.push(code)
        }
    })
    codesToReplace.forEach(code => {
        const swatch = buildSwatch(code.textContent.trim())
        code.parentNode.replaceChild(swatch, code)
    })

    // ── Pass 2: text nodes outside code/pre/etc ──
    const textNodes = []
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
            // Walk up to check for forbidden ancestors
            let el = node.parentElement
            while (el && el !== root) {
                if (SKIP_TAGS.has(el.tagName) || el.tagName === 'CODE') return NodeFilter.FILTER_REJECT
                if (el.hasAttribute('contenteditable')) return NodeFilter.FILTER_REJECT
                if (el.classList.contains('color-swatch')) return NodeFilter.FILTER_REJECT
                el = el.parentElement
            }
            return COLOR_RE.test(node.textContent)
                ? NodeFilter.FILTER_ACCEPT
                : NodeFilter.FILTER_REJECT
        },
    })

    while (walker.nextNode()) textNodes.push(walker.currentNode)

    textNodes.forEach(textNode => {
        const text = textNode.textContent
        COLOR_RE.lastIndex = 0
        const parts = []
        let lastIndex = 0
        let match

        while ((match = COLOR_RE.exec(text)) !== null) {
            // Add any text before this match
            if (match.index > lastIndex) {
                parts.push(document.createTextNode(text.slice(lastIndex, match.index)))
            }
            parts.push(buildSwatch(match[0]))
            lastIndex = COLOR_RE.lastIndex
        }

        if (parts.length === 0) return

        // Add trailing text
        if (lastIndex < text.length) {
            parts.push(document.createTextNode(text.slice(lastIndex)))
        }

        // Replace the text node with the new fragments
        const frag = document.createDocumentFragment()
        parts.forEach(p => frag.appendChild(p))
        textNode.parentNode.replaceChild(frag, textNode)
    })
}

function buildSwatch(colorStr) {
    const wrapper = document.createElement('span')
    wrapper.className = 'color-swatch'

    const box = document.createElement('span')
    box.className = 'color-swatch-box'
    box.style.background = colorStr

    const label = document.createElement('strong')
    label.textContent = colorStr

    wrapper.appendChild(box)
    wrapper.appendChild(label)
    return wrapper
}
